import {
  buildSankey,
  type EntitySummary,
  FLOWS_MAX_FOCUS,
  type FlowFilter,
  type FlowItem,
  flowCurrencies,
  flowMatches,
  flowValue,
  formatAmount,
  GRAPH_STATUSES,
  linkAmountLabel,
  NO_CURRENCY,
  parseFlowIds,
  type SankeyLinkData,
} from '@hoopoe/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Banknote, Loader2, Lock, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { ColorLegend } from '../components/colors/color-by';
import { DataText } from '../components/data/data-text';
import { EntityLink, REVIEW_STATUS_LABELS, StatusBadge } from '../components/entities/entity-link';
import { SankeyChart, type SankeySelection } from '../components/flows/sankey-chart';
import { Section } from '../components/graph/panels';
import { EmptyState } from '../components/guidance/empty-state';
import { EntityAutocomplete } from '../components/guidance/entity-autocomplete';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { ApiError } from '../lib/api';
import { useStyleOf } from '../lib/colors';
import { useEntitySearch } from '../lib/entities';
import { useFlows } from '../lib/flows';
import { useSchema } from '../lib/schema';

// Money-flow Sankey (PLAN §9; task 35): asset / transaction flows between Actors, filterable by period and currency.
// Flows come from flow claims ("donated to", "paid", "gave a grant to", "funded by" read the other way round) and
// the rows of each actor's "Funding sources" table. One chart shows one currency (amounts in different currencies
// are never added up); undisclosed amounts are drawn as thin grey bands. The focus actors and the currency live in
// the URL, so a view can be shared or opened from a profile.

export interface FlowsSearch {
  /** Focus actor ids, comma-separated; none = every flow. */
  ids?: string;
  currency?: string;
}

const CURRENCY = /^([A-Z]{3}|none)$/;

export const Route = createFileRoute('/flows')({
  validateSearch: (s: Record<string, unknown>): FlowsSearch => {
    const ids = parseFlowIds(s.ids);
    const currency = typeof s.currency === 'string' && CURRENCY.test(s.currency) ? s.currency : undefined;
    return { ...(ids.length ? { ids: ids.join(',') } : {}), ...(currency ? { currency } : {}) };
  },
  component: FlowsPage,
});

const TOUR = {
  id: 'flows',
  steps: [
    {
      target: '[data-tour="flows-focus"]',
      title: 'Follow the money of an actor',
      body: 'Add an actor to see only the flows it pays or receives. Leave it empty to see every flow.',
      learnMore: 'money-flows',
    },
    {
      target: '[data-tour="flows-filters"]',
      title: 'One currency at a time',
      body: 'Amounts in different currencies are never added up. Pick the currency, the years and the review statuses.',
    },
    {
      target: '[data-tour="flows-chart"]',
      title: 'Read the Sankey',
      body: 'Payers on the left, recipients on the right. A band is as wide as the money it carries; grey bands are undisclosed amounts.',
      learnMore: 'money-flows',
    },
  ],
};

const year = (v: string) => (/^\d{4}$/.test(v) ? Number(v) : null);
const currencyName = (k: string) => (k === NO_CURRENCY ? 'No currency stated' : k);

function FlowsPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/flows' });
  const entitySearch = useEntitySearch(snap);
  const styleOf = useStyleOf(snap);
  const ids = useMemo(() => parseFlowIds(search.ids), [search.ids]);
  const q = useFlows(ids);
  const [statuses, setStatuses] = useState<NonNullable<FlowFilter['statuses']>>([...GRAPH_STATUSES]);
  const [undisclosed, setUndisclosed] = useState(true);
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [selected, setSelected] = useState<SankeySelection>(null);
  const [now] = useState(() => Date.now());
  const uid = { add: useId(), currency: useId(), from: useId(), to: useId() };

  const go = (next: { ids?: string[]; currency?: string | null }) => {
    const nextIds = next.ids ?? ids;
    const cur = next.currency === undefined ? search.currency : (next.currency ?? undefined);
    setSelected(null);
    navigate({ search: { ...(nextIds.length ? { ids: nextIds.join(',') } : {}), ...(cur ? { currency: cur } : {}) } });
  };
  const data = q.data;
  const entities = useMemo(() => new Map((data?.entities ?? []).map(e => [e.id, e] as const)), [data]);
  const currencies = useMemo(() => flowCurrencies(data?.flows ?? []), [data]);
  const currency = search.currency ?? currencies[0]?.key ?? null;
  const filter: FlowFilter = useMemo(
    () => ({ currency, statuses, undisclosed, fromYear: year(fromYear), toYear: year(toYear) }),
    [currency, statuses, undisclosed, fromYear, toYear],
  );
  const shown = useMemo(() => (data?.flows ?? []).filter(f => flowMatches(f, filter, now)), [data, filter, now]);
  const graph = useMemo(() => buildSankey(shown), [shown]);
  const otherCurrencies = useMemo(
    () => (data?.flows ?? []).filter(f => flowValue(f) !== null && (f.currency ?? NO_CURRENCY) !== currency).length,
    [data, currency],
  );
  const legendStyles = useMemo(
    () =>
      graph.nodes
        .map(n => entities.get(n.id))
        .filter((e): e is EntitySummary => !!e)
        .map(e => styleOf(e)),
    [graph, entities, styleOf],
  );
  const allLinks = useMemo(() => [...graph.links, ...graph.circular], [graph]);
  const selectedLink = selected?.kind === 'link' ? allLinks.find(l => l.id === selected.id) : undefined;
  const selectedNode = selected?.kind === 'node' ? entities.get(selected.id) : undefined;
  const filtersOn = statuses.length < GRAPH_STATUSES.length || !undisclosed || !!fromYear || !!toYear;
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
  const name = (id: string) => entities.get(id)?.displayName ?? 'Unknown actor';

  if (!snap) return <PageHeader title="Money flows" />;

  return (
    <>
      <PageHeader
        title="Money flows"
        description="Who pays whom: donations, grants, payments, investments and funding between actors, in one currency at a time."
      />
      <Tip id="flows" learnMore="money-flows">
        A flow comes from a claim such as “donated to” or “funded by”, or from a row of an actor’s Funding sources table. Ranges count as
        their middle; undisclosed amounts are thin grey bands.
      </Tip>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="min-w-72 flex-1 space-y-1" data-tour="flows-focus">
          <div className="flex items-center gap-1">
            <label htmlFor={uid.add} className="text-xs font-medium">
              Focus on an actor
            </label>
            <FieldHelp
              label="Focus on an actor"
              help={`Shows only the flows this actor pays or receives (up to ${FLOWS_MAX_FOCUS} actors). Leave empty to see every flow.`}
              example="Cedar Fund, حسن حداد or FDN_CEDAR"
              learnMore="money-flows"
            />
          </div>
          <EntityAutocomplete
            key={ids.join(',')}
            id={uid.add}
            placeholder={ids.length >= FLOWS_MAX_FOCUS ? `At most ${FLOWS_MAX_FOCUS} actors` : 'Type a name, alias or ID code'}
            search={async text =>
              ids.length >= FLOWS_MAX_FOCUS ? [] : (await entitySearch(['actor'], text)).filter(s => !ids.includes(s.id))
            }
            onSelect={s => go({ ids: [...ids, s.id] })}
          />
        </div>
        {ids.length > 0 && (
          <Button size="md" variant="ghost" onClick={() => go({ ids: [] })}>
            <X aria-hidden="true" /> Show all flows
          </Button>
        )}
      </div>
      {ids.length > 0 && (
        <ul aria-label="Focus actors" className="mt-2 flex flex-wrap gap-2">
          {ids.map(id => (
            <li key={id} className="flex items-center gap-1 rounded-full border bg-surface ps-2 pe-1 text-sm">
              <DataText className="max-w-60 truncate">{entities.get(id)?.displayName ?? id}</DataText>
              <button
                type="button"
                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={`Remove ${entities.get(id)?.displayName ?? id} from the focus`}
                onClick={() => go({ ids: ids.filter(x => x !== id) })}
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <fieldset className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-lg border bg-surface p-3" data-tour="flows-filters">
        <legend className="sr-only">Filters</legend>
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <label htmlFor={uid.currency} className="block text-xs font-medium">
              Currency
            </label>
            <FieldHelp
              label="Currency"
              help="One chart shows one currency: amounts in different currencies are never added up. Undisclosed amounts with no currency show in every currency."
              example="EUR"
              learnMore="money-flows"
            />
          </div>
          <select
            id={uid.currency}
            value={currency ?? ''}
            onChange={e => go({ currency: e.target.value || null })}
            disabled={!currencies.length}
            className="h-9 rounded-md border border-input bg-surface px-2 text-sm"
          >
            {!currencies.length && <option value="">No amounts yet</option>}
            {currencies.map(c => (
              <option key={c.key} value={c.key}>
                {currencyName(c.key)} ({c.count} {c.count === 1 ? 'flow' : 'flows'})
              </option>
            ))}
          </select>
        </div>
        <fieldset className="space-y-1">
          <legend className="block text-xs font-medium">Review status</legend>
          <div className="flex flex-wrap gap-3 text-sm">
            {GRAPH_STATUSES.map(s => (
              <label key={s} className="flex items-center gap-1.5">
                <input type="checkbox" checked={statuses.includes(s)} onChange={() => setStatuses(v => toggle(v, s))} />
                {REVIEW_STATUS_LABELS[s]}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="flex items-center gap-1.5 self-end pb-2 text-sm">
          <input type="checkbox" checked={undisclosed} onChange={e => setUndisclosed(e.target.checked)} />
          Undisclosed amounts
        </label>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <label htmlFor={uid.from} className="block text-xs font-medium">
              From year
            </label>
            <input
              id={uid.from}
              inputMode="numeric"
              value={fromYear}
              onChange={e => setFromYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 2015"
              className="h-9 w-24 rounded-md border border-input bg-surface px-2 text-sm tabular"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor={uid.to} className="block text-xs font-medium">
              To year
            </label>
            <input
              id={uid.to}
              inputMode="numeric"
              value={toYear}
              onChange={e => setToYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 2024"
              className="h-9 w-24 rounded-md border border-input bg-surface px-2 text-sm tabular"
            />
          </div>
          <FieldHelp
            label="Years"
            help="Keeps the flows whose period overlaps these years (a flow over 2019–2022 stays for 2021). Flows without a date are left out while years are set."
            example="2019 to 2022"
            learnMore="money-flows"
          />
        </div>
        {filtersOn && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setStatuses([...GRAPH_STATUSES]);
              setUndisclosed(true);
              setFromYear('');
              setToYear('');
            }}
          >
            Reset filters
          </Button>
        )}
      </fieldset>

      <div aria-live="polite" className="mt-3 min-h-5 text-sm">
        {q.isFetching ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading the flows…
          </span>
        ) : q.isError ? (
          <span role="alert" className="text-danger-foreground">
            {q.error instanceof ApiError ? q.error.message : 'The flows could not be loaded. Try again.'}
          </span>
        ) : data ? (
          <span className="text-muted-foreground">
            {shown.length} of {data.flows.length} {data.flows.length === 1 ? 'flow' : 'flows'} shown
            {otherCurrencies > 0 && ` · ${otherCurrencies} in other currencies`}
            {data.truncated && ' · some flows were left out: there were too many. Focus on an actor to see all of theirs.'}
          </span>
        ) : null}
      </div>

      <div data-tour="flows-chart" className="mt-1">
        {data && ids.length > 0 && data.focus.length === 0 ? (
          <EmptyState icon={Banknote} title="These actors no longer exist" learnMore="money-flows">
            They may have been merged into another entity. Remove them from the focus or add another actor.
          </EmptyState>
        ) : data && data.flows.length === 0 ? (
          <EmptyState icon={Banknote} title="No money flows yet" learnMore="money-flows">
            Add a “donated to”, “paid”, “gave a grant to” or “funded by” link with an amount on a profile, or fill an actor’s Funding
            sources table.
          </EmptyState>
        ) : data && graph.links.length === 0 && graph.circular.length === 0 ? (
          <EmptyState icon={Banknote} title="No flow matches" learnMore="money-flows">
            {filtersOn ? 'Reset the filters or pick another currency.' : 'Pick another currency.'}
          </EmptyState>
        ) : data ? (
          graph.links.length > 0 ? (
            <SankeyChart
              snap={snap}
              graph={graph}
              entities={entities}
              currency={currency}
              selected={selected}
              onSelect={s => setSelected(cur => (cur && s && cur.kind === s.kind && cur.id === s.id ? null : s))}
            />
          ) : null
        ) : null}
      </div>

      {data && allLinks.length > 0 && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-4">
            {selectedLink && <LinkDetails link={selectedLink} entities={entities} currency={currency} onClose={() => setSelected(null)} />}
            {selectedNode && (
              <Section
                title="Actor"
                actions={
                  <Button size="sm" variant="ghost" onClick={() => setSelected(null)} aria-label="Close details">
                    <X aria-hidden="true" />
                  </Button>
                }
              >
                <div className="space-y-2 text-sm" data-testid="flows-node-details">
                  <EntityLink schema={snap} entity={selectedNode} />
                  <p>
                    Paid {formatAmount(graph.nodes.find(n => n.id === selectedNode.id)?.paid ?? 0, currency)} · received{' '}
                    {formatAmount(graph.nodes.find(n => n.id === selectedNode.id)?.received ?? 0, currency)} on this chart.
                  </p>
                  {!ids.includes(selectedNode.id) && ids.length < FLOWS_MAX_FOCUS && (
                    <Button size="sm" variant="secondary" onClick={() => go({ ids: [...ids, selectedNode.id] })}>
                      Focus on {selectedNode.displayName}
                    </Button>
                  )}
                </div>
              </Section>
            )}
            <Section title={`Links (${allLinks.length})`}>
              <div className="relative overflow-x-auto">
                <table className="w-full text-sm" aria-label="Money flow links">
                  <thead>
                    <tr className="border-b text-start text-xs text-muted-foreground">
                      <th scope="col" className="py-1 pe-2 text-start font-medium">
                        From
                      </th>
                      <th scope="col" className="py-1 pe-2 text-start font-medium">
                        To
                      </th>
                      <th scope="col" className="py-1 pe-2 text-start font-medium">
                        Amount
                      </th>
                      <th scope="col" className="py-1 pe-2 text-start font-medium">
                        Status
                      </th>
                      <th scope="col" className="py-1 text-start font-medium">
                        <span className="sr-only">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allLinks.map(l => {
                      const circular = graph.circular.includes(l);
                      return (
                        <tr key={l.id} className="border-b last:border-b-0" data-link-row={l.id}>
                          <td className="py-1 pe-2">
                            <DataText>{name(l.source)}</DataText>
                          </td>
                          <td className="py-1 pe-2">
                            <DataText>{name(l.target)}</DataText>
                          </td>
                          <td className="py-1 pe-2 tabular">
                            {linkAmountLabel(l, currency)}
                            {circular && <span className="block text-xs text-muted-foreground">Not drawn: it closes a loop</span>}
                          </td>
                          <td className="py-1 pe-2">
                            <span className="inline-flex items-center gap-1">
                              <StatusBadge status={l.status} />
                              {l.sensitivity === 'confidential' && (
                                <Lock className="size-3.5 text-muted-foreground" aria-label="Confidential" />
                              )}
                            </span>
                          </td>
                          <td className="py-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-pressed={selected?.kind === 'link' && selected.id === l.id}
                              onClick={() => setSelected({ kind: 'link', id: l.id })}
                              aria-label={`Details: ${name(l.source)} → ${name(l.target)}`}
                            >
                              Details
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
          <Section title="Legend">
            {legendStyles.length > 0 && <ColorLegend styles={legendStyles} />}
            <p className="mt-2 text-xs text-muted-foreground">
              Payers on the left, recipients on the right; a band’s width is the money it carries in{' '}
              {currency ? currencyName(currency) : 'the currency'}. Band color = the payer’s color. Solid band = verified, dashed =
              unreviewed, dotted = disputed. Grey band = amount undisclosed (its width means nothing). Ranges count as their middle.{' '}
              <Lock className="inline size-3" aria-label="lock" /> = confidential.
            </p>
          </Section>
        </div>
      )}
    </>
  );
}

function LinkDetails({
  link,
  entities,
  currency,
  onClose,
}: {
  link: SankeyLinkData;
  entities: Map<string, EntitySummary>;
  currency: string | null;
  onClose(): void;
}) {
  const { data: snap } = useSchema();
  const from = entities.get(link.source);
  const to = entities.get(link.target);
  return (
    <Section
      title="Flow"
      actions={
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close details">
          <X aria-hidden="true" />
        </Button>
      }
    >
      <div data-testid="flows-details" className="space-y-3 text-sm">
        <p className="flex flex-wrap items-center gap-1.5">
          {from && snap ? <EntityLink schema={snap} entity={from} /> : null}
          <span aria-hidden="true">→</span>
          <span className="sr-only">pays</span>
          {to && snap ? <EntityLink schema={snap} entity={to} /> : null}
        </p>
        <p className="font-medium tabular">{linkAmountLabel(link, currency)}</p>
        <ul aria-label="Flows in this link" className="space-y-2">
          {link.flows.map(f => (
            <FlowRow key={f.id} flow={f} />
          ))}
        </ul>
      </div>
    </Section>
  );
}

function FlowRow({ flow }: { flow: FlowItem }) {
  return (
    <li className="rounded-md border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{flow.label}</span>
        <DataText className="tabular">{flow.amountText ?? 'no amount'}</DataText>
        {flow.periodText || flow.periodFrom ? (
          <span className="text-muted-foreground">· {flow.periodText ?? [flow.periodFrom, flow.periodTo].filter(Boolean).join(' – ')}</span>
        ) : (
          <span className="text-muted-foreground">· no date</span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <StatusBadge status={flow.status} />
        {flow.sensitivity === 'confidential' && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="size-3" aria-hidden="true" /> Confidential
          </span>
        )}
        <Link to="/entity/$id" params={{ id: flow.subjectId }} className="text-xs font-medium underline-offset-2 hover:underline">
          Open the profile it is recorded on
        </Link>
      </div>
    </li>
  );
}
